import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textColor = scrolled ? 'text-foreground-900' : 'text-background-50';
  const textHover = scrolled ? 'hover:text-foreground-700' : 'hover:text-background-200';
  const brandColor = scrolled ? 'text-foreground-900' : 'text-background-50';
  const brandAccent = scrolled ? 'text-accent-600' : 'text-background-50/80';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <span className={`font-heading text-xl md:text-2xl font-semibold tracking-tight transition-colors duration-300 ${brandColor}`}>
              Hoobuy<span className={`transition-colors duration-300 ${brandAccent}`}>.</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer font-medium ${textColor} ${textHover}`}
            >
              Início
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm transition-colors whitespace-nowrap cursor-pointer bg-transparent border-none font-medium ${textColor} ${textHover}`}
              >
                Categorias
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200/80 rounded-lg shadow-lg py-2 w-56 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                        <i className={`${cat.icon} text-xs text-accent-600`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/artigo/hoobuy-sheet-guia-completo-2025"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer font-medium ${textColor} ${textHover}`}
            >
              Guia de Compras
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer font-medium ${textColor} ${textHover}`}
            >
              Hoobuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-background-50 text-foreground-900 text-sm font-medium rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer bg-transparent border-none ${textColor}`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-4">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="text-sm text-foreground-900 hover:text-foreground-700 font-medium transition-colors py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                <i className="ri-home-4-line text-accent-600"></i>
              </span>
              Início
            </Link>

            <Link
              to="/artigo/hoobuy-sheet-guia-completo-2025"
              className="text-sm text-foreground-900 hover:text-foreground-700 font-medium transition-colors py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                <i className="ri-book-open-line text-accent-600"></i>
              </span>
              Guia de Compras
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm text-foreground-900 hover:text-foreground-700 font-medium transition-colors py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                <i className="ri-external-link-line text-accent-600"></i>
              </span>
              Hoobuy Sheet
            </a>

            <div className="border-t border-background-200/60 mt-2 pt-2">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full text-sm text-foreground-900 font-semibold py-3 px-2 rounded-lg hover:bg-background-100 cursor-pointer bg-transparent border-none transition-colors"
              >
                <span>
                  <span className="w-5 h-5 inline-flex items-center justify-center mr-2">
                    <i className="ri-grid-line text-accent-600"></i>
                  </span>
                  Categorias
                </span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 mt-1 pl-7">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-colors py-2.5 px-2 rounded-lg cursor-pointer flex items-center gap-1.5"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <i className={`${cat.icon} text-accent-600 text-xs`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 px-5 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}